import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getLeasesHome } from "./constants/leaseRoutes";
import {
  FiKey,
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiRotateCcw,
  FiInbox,
  FiEdit2,
  FiTrash2,
  FiMessageSquare,
  FiMapPin,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification, { showToast } from "../../components/common/notification/Notification";
import Spinner from "../../components/common/spinner/Spinner";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import { createLeaseService } from "./services/leaseService";
import {
  LEASE_STATUSES,
  LEASE_DEFAULT_PAGE_SIZE,
  LEASE_FETCH_PAGE_SIZE,
} from "./constants/leaseEndpoints";
import LeaseNotesModal from "./components/LeaseNotesModal";
import "../property/PropertiesPage.css";
import "../property/components/PropertyPageHeader.css";
import "../property/components/PropertyFilterToolbar.css";
import usePersistentState from "../../hooks/usePersistentState";
import "./LeasesPage.css";
import { formatDate as formatDateSafe } from "../../utils/date";

const getStatusMeta = (status) =>
  LEASE_STATUSES.find((s) => s.value === String(status || "").toUpperCase()) || {
    value: "",
    label: status || "—",
    variant: "secondary",
  };

const formatDate = (value) => formatDateSafe(value, { fallback: "" });

const LeasesPage = () => {
  const navigate = useNavigate();
  const leasesHome = getLeasesHome(useLocation().pathname);
  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId = authUser?.user?.id || authUser?.id || authUser?.userDetails?.id;

  const apiClient = usePropertyApiClient();
  const leaseService = useMemo(() => createLeaseService(apiClient), [apiClient]);

  // List state
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = usePersistentState("list:leases:page", 1);

  // Filters
  const [searchTerm, setSearchTerm] = usePersistentState("list:leases:search", "");
  const [selectedStatus, setSelectedStatus] = usePersistentState("list:leases:status", "");
  const [viewMode, setViewMode] = usePersistentState("list:leases:view", "grid");

  // Dialogs (delete confirmation and notes; create / edit are full pages)
  const [leaseToDelete, setLeaseToDelete] = useState(null);
  const [notesLease, setNotesLease] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Backend scopes this list to the logged-in customer via the auth token
  const fetchLeases = useCallback(async () => {
    try {
      setLoading(true);
      const data = await leaseService.listMy({
        page_number: 1,
        page_size: LEASE_FETCH_PAGE_SIZE,
        order_by: "DESC",
      });
      setLeases(data?.leases || []);
      setError(null);
    } catch (err) {
      console.warn("Error fetching leases:", err);
      setError(err?.message || "Unable to load leases. Please check your network connection.");
      setLeases([]);
    } finally {
      setLoading(false);
    }
  }, [leaseService]);

  useEffect(() => {
    fetchLeases();
  }, [fetchLeases]);

  const filteredLeases = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return leases.filter((l) => {
      const matchSearch =
        !q ||
        [l.title, l.description, l.city, l.state, l.purpose?.name].some(
          (v) => v && String(v).toLowerCase().includes(q)
        );
      const matchStatus = !selectedStatus || String(l.status || "").toUpperCase() === selectedStatus;
      return matchSearch && matchStatus;
    });
  }, [leases, searchTerm, selectedStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredLeases.length / LEASE_DEFAULT_PAGE_SIZE));
  const pagedLeases = filteredLeases.slice(
    (currentPage - 1) * LEASE_DEFAULT_PAGE_SIZE,
    currentPage * LEASE_DEFAULT_PAGE_SIZE
  );
  const hasFilters = Boolean(searchTerm || selectedStatus);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, setCurrentPage]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  const handleStatusChange = async (lease, status) => {
    if (!status || status === String(lease.status || "").toUpperCase()) return;
    try {
      await leaseService.updateStatus(lease.id, status);
      showToast("Lease status updated", { type: "success" });
      fetchLeases();
    } catch (err) {
      showToast(err?.response?.data?.text || err?.message || "Failed to update status", { type: "error" });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await leaseService.remove(leaseToDelete.id);
      showToast("Lease deleted successfully", { type: "success" });
      setLeaseToDelete(null);
      fetchLeases();
    } catch (err) {
      showToast(err?.response?.data?.text || err?.message || "Failed to delete lease", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const renderStatus = (lease) => {
    const meta = getStatusMeta(lease.status);
    return (
      <select
        className={`form-select form-select-sm lease-status-select lease-status-select--${meta.variant}`}
        value={String(lease.status || "").toUpperCase()}
        onChange={(e) => handleStatusChange(lease, e.target.value)}
        title="Change status"
      >
        {!meta.value && <option value="">{meta.label}</option>}
        {LEASE_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    );
  };

  const renderActions = (lease) => (
    <div className="d-flex gap-2">
      <button type="button" className="btn btn-sm btn-outline-secondary" title="Notes" onClick={() => setNotesLease(lease)}>
        <FiMessageSquare size={14} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => navigate(`${leasesHome}/${lease.id}/edit`, { state: { lease } })}>
        <FiEdit2 size={14} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setLeaseToDelete(lease)}>
        <FiTrash2 size={14} />
      </button>
    </div>
  );

  return (
    <div className="properties-module-page">
      <Notification />

      {/* Page Header */}
      <div className="property-page-header">
        <div className="property-page-header__content">
          <div className="property-page-header__badge">
            <FiKey className="property-page-header__badge-icon" />
            <span>Lease Hub</span>
            {!loading && !error && (
              <span className="property-page-header__count-tag">{leases.length} Listed</span>
            )}
          </div>
          <h1 className="property-page-header__title">My Leases</h1>
          <p className="property-page-header__description">
            Lease requests you have created. Only leases created by your account appear here.
          </p>
        </div>
        <div className="property-page-header__actions">
          <button type="button" className="property-page-header__create-btn" onClick={() => navigate(`${leasesHome}/new`)}>
            <FiPlus size={18} />
            <span>Add Lease</span>
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="property-filter-toolbar">
        <div className="property-filter-toolbar__controls">
          <form className="property-filter-toolbar__search" onSubmit={(e) => e.preventDefault()}>
            <FiSearch className="property-filter-toolbar__search-icon" />
            <input
              type="text"
              placeholder="Search title, description, purpose, city..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="property-filter-toolbar__search-input"
            />
            {searchTerm && (
              <button type="button" className="property-filter-toolbar__clear-btn" onClick={() => setSearchTerm("")}>
                ×
              </button>
            )}
          </form>

          <div className="property-filter-toolbar__selects">
            <div className="property-filter-select-wrap">
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="property-filter-select"
              >
                <option value="">All Statuses</option>
                {LEASE_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <button type="button" className="property-filter-reset-btn" onClick={handleResetFilters} title="Reset Filters">
                <FiRotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="property-view-toggle">
            <button
              type="button"
              className={`property-view-toggle__btn ${viewMode === "grid" ? "property-view-toggle__btn--active" : ""}`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <FiGrid size={16} />
            </button>
            <button
              type="button"
              className={`property-view-toggle__btn ${viewMode === "table" ? "property-view-toggle__btn--active" : ""}`}
              onClick={() => setViewMode("table")}
              title="List / Table View"
            >
              <FiList size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="properties-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading leases...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchLeases}>
            Retry
          </button>
        </div>
      ) : pagedLeases.length === 0 ? (
        <div className="properties-empty-state">
          <div className="properties-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="properties-empty-state__title">
            {hasFilters ? "No Matching Leases" : "You haven't created any leases yet"}
          </h3>
          <p className="properties-empty-state__text">
            {hasFilters
              ? "Try adjusting your search terms or filters."
              : "Leases you add will show up here."}
          </p>
          {hasFilters ? (
            <button type="button" className="btn btn-outline-success mt-2" onClick={handleResetFilters}>
              Clear Filters
            </button>
          ) : (
            <button type="button" className="btn btn-primary mt-2" onClick={() => navigate(`${leasesHome}/new`)}>
              Add Lease
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="properties-grid">
          {pagedLeases.map((lease) => (
            <div key={lease.id} className="lease-card">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <span className="badge bg-primary-subtle text-primary fw-semibold">
                  {lease.purpose?.name || "Lease"}
                </span>
                {renderStatus(lease)}
              </div>
              <h5 className="fw-bold text-dark mb-1 text-truncate" title={lease.title}>
                {lease.title || "Untitled lease"}
              </h5>
              <p className="text-muted small lease-card__desc">{lease.description || "No description provided."}</p>
              <div className="d-flex flex-column gap-1 small mb-3">
                <span className="fw-semibold text-dark">
                  Budget: ₹{lease.budget != null ? Number(lease.budget).toLocaleString("en-IN") : "N/A"}
                </span>
                <span className="text-secondary">
                  <FiMapPin className="me-1" />
                  {[lease.city, lease.state].filter(Boolean).join(", ") || "Location N/A"}
                </span>
                {lease.created_on && (
                  <span className="text-secondary">
                    <FiCalendar className="me-1" />
                    {formatDate(lease.created_on)}
                  </span>
                )}
              </div>
              {renderActions(lease)}
            </div>
          ))}
        </div>
      ) : (
        <div className="table-responsive bg-white rounded-3 border">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>Purpose</th>
                <th>Budget</th>
                <th>Location</th>
                <th>Created</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pagedLeases.map((lease) => (
                <tr key={lease.id}>
                  <td className="fw-semibold">{lease.title || "Untitled lease"}</td>
                  <td>{lease.purpose?.name || "—"}</td>
                  <td>₹{lease.budget != null ? Number(lease.budget).toLocaleString("en-IN") : "N/A"}</td>
                  <td>{[lease.city, lease.state].filter(Boolean).join(", ") || "—"}</td>
                  <td>{formatDate(lease.created_on) || "—"}</td>
                  <td style={{ minWidth: 150 }}>{renderStatus(lease)}</td>
                  <td>{renderActions(lease)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Bar */}
      {totalPages > 1 && !loading && !error && (
        <div className="properties-pagination-bar">
          <button
            type="button"
            className="properties-page-nav-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>
          <span className="properties-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            className="properties-page-nav-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Notes */}
      <LeaseNotesModal
        show={Boolean(notesLease)}
        onHide={() => setNotesLease(null)}
        lease={notesLease}
        leaseService={leaseService}
        currentUserId={currentUserId}
        onChanged={fetchLeases}
      />

      {/* Delete confirmation */}
      {leaseToDelete && (
        <div className="lease-confirm-backdrop" role="dialog" aria-modal="true">
          <div className="lease-confirm">
            <h5 className="fw-bold">Delete lease?</h5>
            <p className="text-muted mb-4">
              "{leaseToDelete.title || `#${leaseToDelete.id}`}" will be removed from your leases.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setLeaseToDelete(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeasesPage;
